import { useCallback, useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import type { AdditionalVehicleDetailsFormInputs } from '../schemas/additionalvehicledetails.schema';

interface Options {
    autoSubmit: boolean;
    isValid: boolean;
    watchedValues: AdditionalVehicleDetailsFormInputs;
    onSubmit: (data: AdditionalVehicleDetailsFormInputs) => void;
}

export function useVehicleDetailsAutoSubmit({ autoSubmit, isValid, watchedValues, onSubmit }: Options) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastSubmittedData, setLastSubmittedData] = useState<string>('');

    const handleFormSubmit: SubmitHandler<AdditionalVehicleDetailsFormInputs> = useCallback(
        async (data) => {
            setIsSubmitting(true);
            try {
                await onSubmit(data);
            } finally {
                setIsSubmitting(false);
            }
        },
        [onSubmit]
    );

    // Auto-submit when form is valid and values change
    useEffect(() => {
        if (autoSubmit && isValid) {
            const currentData = JSON.stringify(watchedValues);

            if (currentData !== lastSubmittedData) {
                const submitData = async () => {
                    try {
                        await handleFormSubmit(watchedValues);
                        setLastSubmittedData(currentData);
                    } catch (error) {
                        console.error('Auto-submit failed:', error);
                    }
                };

                const timeoutId = setTimeout(submitData, 500);
                return () => clearTimeout(timeoutId);
            }
        }
    }, [handleFormSubmit, watchedValues, isValid, autoSubmit, lastSubmittedData]);

    const clearLastSubmitted = useCallback(() => setLastSubmittedData(''), []);

    return { isSubmitting, handleFormSubmit, clearLastSubmitted };
}
